import { useEffect, useMemo, useState } from 'react';

import { apiFetch } from '../../../utils';
import { useModal } from '../modal';

import './index.css';

const CharacterSearchModal = () => {
    const { closeModal } = useModal();

    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [results, setResults] = useState([]);

    const trimmedQuery = useMemo(() => query.trim(), [query]);

    useEffect(() => {
        let cancelled = false;

        if (!trimmedQuery) {
            setLoading(false);
            setErrors([]);
            setResults([]);
            return () => {
                cancelled = true;
            };
        }

        const timer = setTimeout(async () => {
            setLoading(true);
            setErrors([]);

            const response = await apiFetch('/user/character/search', 'POST', {
                query: trimmedQuery,
                limit: 20,
            });

            if (cancelled) return;

            if (!response || response.code !== 'success') {
                const err = response?.data?.msg || ['Failed to search characters.'];
                setErrors(Array.isArray(err) ? err : ['Failed to search characters.']);
                setResults([]);
                setLoading(false);
                return;
            }

            setResults(Array.isArray(response.data) ? response.data : []);
            setLoading(false);
        }, 180);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [trimmedQuery]);

    const openCharacter = (id) => {
        closeModal();
        window.location.href = `/character/${id}`;
    };

    return (
        <div id='character-search-modal'>
            <div id='character-search-shell'>
                <div className='modal-panel-header'>
                    <h2 className='modal-panel-title'>Character Search</h2>
                    <button
                        type='button'
                        className='button modal-panel-close'
                        title='Close character search'
                        onClick={closeModal}
                    >
                        Close
                    </button>
                </div>

                <div id='character-search-controls'>
                    <label htmlFor='character-search-input'>Character Name</label>
                    <input
                        id='character-search-input'
                        className='character-search-input'
                        type='text'
                        maxLength='20'
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder='Start typing a character name'
                        autoComplete='off'
                    />
                </div>

                <div id='character-search-results-wrap'>
                    {errors.map((error) => <p className='modal-error-message' key={error}>{error}</p>)}

                    {!errors.length && (
                        <table id='character-search-table'>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Level</th>
                                    <th>Vocation</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!trimmedQuery && (
                                    <tr>
                                        <td colSpan='4' className='character-search-empty'>Type a name to search.</td>
                                    </tr>
                                )}

                                {trimmedQuery && loading && (
                                    <tr>
                                        <td colSpan='4' className='character-search-empty'>Searching...</td>
                                    </tr>
                                )}

                                {trimmedQuery && !loading && results.length === 0 && (
                                    <tr>
                                        <td colSpan='4' className='character-search-empty'>No matching characters.</td>
                                    </tr>
                                )}

                                {trimmedQuery && !loading && results.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.name}</td>
                                        <td>{Number(row.level || 0).toLocaleString()}</td>
                                        <td>{Number(row.vocation || 0)}</td>
                                        <td>
                                            <button
                                                type='button'
                                                className='button character-search-view-button'
                                                onClick={() => openCharacter(row.id)}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CharacterSearchModal;
